import type { Schemas } from '@npc/api-client'
import { useState } from 'react'

import { Button } from '../../components/button'
import { T } from '../../copy/text'
import { useRecheckHost } from '../../app/queries'

/**
 * 「它是不是搬到另一台机器上去了」那一行。
 *
 * ## 它与名字那一行是两件事，所以是两行两个确认
 *
 * 一条建议里可能同时有「我猜这台叫小食堂」和「它是不是搬家了」。
 * **这两件事要求用户做的下一个动作不一样**——合成一个按钮的话，
 * 他为了确认名字点一下，**顺手把一次归属迁移也一起点了，而那件事他根本没看**。
 *
 * ## 四档，按「哪一半认得出来」分
 *
 * 两个名字各自可能是空的（`hostName()` 在取不到时返回 `""`）。
 * 而**空串填进模板不是「缺字的话」，是整张卡片一个字都出不来**——
 * 流 0 真喂空串试过一次：渲染器直接报错、返回空串，
 * **归属冲突从此没有出口，而那个确认按钮挂在一句空话下面**。
 *
 * ## 按钮的判据写在按钮上，不写在档上
 *
 * 每个按钮需要哪个名字，就由那个名字在不在决定：
 *
 * | | 说什么 | 能点什么 |
 * |---|---|---|
 * | 两边都有 | `suggest` | 对 ／ 不对 ／ 重新看 |
 * | 只有 observed | `observed_only` | **只有「对」** ／ 重新看 |
 * | 只有 recorded | `recorded_only` | **只有「不对」** ／ 重新看 |
 * | 两边都没有 | `neither` | **只有「重新看」** |
 *
 * 写在按钮上而不是写在档上，是因为**加一档时不用回来改四处**——
 * 这两者在今天长得一样，在下一次改契约时就不一样了。
 */
export function ReparentRow({
  host,
  onAccept,
  onReject,
  accepting,
}: {
  host: Schemas['HostSummary']
  onAccept: (hostId: string, what: 'identity' | 'parent') => void
  /** 「不对」那两档。必须落到 accept what=reject_standalone / reject_keep，不能只在这一页收掉。 */
  onReject?: (hostId: string, kind: 'standalone' | 'keep_recorded') => void
  accepting: boolean
}) {
  const recheck = useRecheckHost(host.id)
  const [recheckedOnce, setRecheckedOnce] = useState(false)

  const observed = host.suggestion?.parent_observed_name?.trim() ?? ''
  const recorded = host.suggestion?.parent_recorded_name?.trim() ?? ''

  // 归属冲突才有这一行。**档案里空着时平台直接填、根本不问**（契约原话）
  if (!host.suggestion?.parent_host_id) return null

  /**
   * **探成功了、名字还是没有** —— 这一档必须单独说。
   *
   * 不说的话，卡片原样退回 `.neither` 配同一个按钮，
   * **他可以一直点下去，而每一次都「成功」**——
   * 一个每次都成功、每次都回到原地的按钮，比一个报错的按钮更难看穿。
   *
   * 它的下一步是**什么都不用做**，与 `host.access.original.none`
   * 和 `repo.gate.blocked.our_environment` 同一族。
   */
  const stillUnknown = recheckedOnce && recheck.isSuccess && !observed && !recorded

  return (
    <div className="mt-npc-gap-sm border-t border-npc-amber-border pt-npc-gap-sm">
      <p className="m-0 text-npc-caption leading-[1.65] text-npc-amber-ink">
        {stillUnknown ? (
          <T id="host.recheck.still_unknown" />
        ) : observed && recorded ? (
          <T
            id="host.reparent.suggest"
            vars={{ parent_observed_name: observed, parent_recorded_name: recorded }}
          />
        ) : observed ? (
          <T id="host.reparent.suggest.observed_only" vars={{ parent_observed_name: observed }} />
        ) : recorded ? (
          <T id="host.reparent.suggest.recorded_only" vars={{ parent_recorded_name: recorded }} />
        ) : (
          <T id="host.reparent.suggest.neither" />
        )}
      </p>

      {/* 探测中那一句。**必须显示**：这个动作要真连一次 SSH，
          几十秒没反馈他会以为按钮坏了，然后连点几下 */}
      {recheck.isPending ? (
        <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          <T id="host.recheck.running" />
        </p>
      ) : null}

      {/* 连不上。**不复用 error.upstream_down**——那条承诺「我会一直重试」，
          而这里没有那个「一直」，只有每 6 小时一次的例行探测。
          承诺一个我们没有的重试频率，他就不会再回来点，
          **而这一档恰恰只有他回来点才走得出去** */}
      {recheck.isError ? (
        <p className="mb-0 mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-amber-ink">
          <T id="host.recheck.failed" />
        </p>
      ) : null}

      {stillUnknown ? null : (
        <div className="mt-npc-gap-sm flex flex-wrap gap-npc-gap-xs">
          {/* 「对，它在「X」上」——模板的变量叫 parent_name，
              值来自 parent_observed_name。**映射一下，不是缺模板** */}
          {observed ? (
            <Button
              size="sm"
              variant="primary"
              disabled={accepting || recheck.isPending}
              className="relative z-10"
              onClick={(event) => {
                event.stopPropagation()
                onAccept(host.id, 'parent')
              }}
            >
              <T id="host.list.parent_accept" vars={{ parent_name: observed }} />
            </Button>
          ) : null}

          {/* 「不对，它还在「X」上」。**与 host.list.parent_reject
              （「它是单独一台」）不是一回事**——那句是「它谁的也不住」，
              这句是「它还在原来那台上」。按错的那个会把档案改成一个更远的错，
              而他点的时候以为自己在纠正我们 */}
          {recorded ? (
            <Button
              size="sm"
              disabled={accepting || recheck.isPending}
              className="relative z-10"
              onClick={(event) => {
                event.stopPropagation()
                onReject?.(host.id, 'keep_recorded')
              }}
            >
              <T id="host.reparent.reject" vars={{ parent_recorded_name: recorded }} />
            </Button>
          ) : null}

          {/* 「不对，它是单独一台」。没有它，他只能点「对」或者不管，
              而不管会让这条建议一直挂着。 */}
          <Button
            size="sm"
            disabled={accepting || recheck.isPending}
            className="relative z-10"
            onClick={(event) => {
              event.stopPropagation()
              onReject?.(host.id, 'standalone')
            }}
          >
            <T id="host.list.parent_reject" />
          </Button>

          <Button
            size="sm"
            disabled={recheck.isPending}
            className="relative z-10"
            onClick={(event) => {
              event.stopPropagation()
              setRecheckedOnce(true)
              recheck.mutate()
            }}
          >
            <T id="host.recheck.action" />
          </Button>
        </div>
      )}
    </div>
  )
}
